import * as React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { TriCheckbox, WorkspaceButton, WorkspaceNotice } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { CriticalityChip } from '@/components/engagement-requirements/criticality';
import { asCriticality } from '@/components/engagement-requirements/requirements-logic';
import { formatDate } from '@/components/requirement-library/library-logic';
import { cn } from '@/lib/utils/cn';
import {
  IMPORT_FIELDS,
  type ImportField,
  type ImportOverrides,
  type ImportPreview,
} from '@/lib/api/requirement-spreadsheets';
import { metadataEntries } from '@/lib/api/requirement-library';
import {
  FIELD_LABEL,
  IMPORT_CRITICALITY_OPTIONS,
  displayRows,
  duplicateLabel,
  duplicateRefs,
  duplicateScore,
  duplicateTarget,
  fileBadge,
  formatBytes,
  importTotals,
  plural,
  rowsForTab,
  withColumnMapping,
  withCriticality,
  withModule,
  withSheetOverride,
  type DisplayRow,
  type ImportTab,
} from './import-logic';
import { ModuleSelect, SKIP_MODULE, type ModuleOption } from './module-select';

/**
 * The review of one uploaded spreadsheet (T-0153; E11-F06, E11-F03).
 *
 * Reference: `renderImport` in `docs/requirement-library/prototype/
 * selection.js`: the file header, four stat tiles, sheet pills that turn
 * a sheet on or off, a Columns card and a value-mapping card with the
 * uncertain guesses highlighted, and the preview table with its tabs.
 *
 * Shared by the engagement import and the library intake. The library
 * variant has no criticality or module mapping (the library is neutral,
 * amendment 01 Decision 31, and the module is the one being edited): it
 * says what it ignored instead, and lists the rows it refuses.
 *
 * Everything shown is the server's latest preview. A change here calls
 * `onOverrides`; the owner previews again and passes the new preview in.
 * Keeping or skipping a likely duplicate is the one choice held by the
 * owner and not by the server (`keep`), sent with the confirm.
 */

const PAGE_SIZE = 200;

const SELECT =
  'h-[30px] w-full min-w-0 rounded-card border px-2 text-body-sm text-ink-900 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-amber-600 disabled:cursor-not-allowed disabled:opacity-50';
const SELECT_PLAIN = 'border-ink-200 bg-paper-0 hover:border-ink-300';
const SELECT_UNSURE = 'border-posture-at-risk bg-posture-at-risk-tint';
const SELECT_REQUIRED = 'border-posture-critical bg-posture-critical-tint';

const CHIP =
  'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-meta font-medium';
const CHIP_HEALTHY = 'bg-posture-healthy-tint text-posture-healthy';
const CHIP_AT_RISK = 'bg-posture-at-risk-tint text-posture-at-risk';
const CHIP_CRITICAL = 'bg-posture-critical-tint text-posture-critical';
const CHIP_MUTED = 'bg-ink-100 text-posture-unassessed';

const CARD = 'rounded-card border border-ink-100 bg-paper-0 p-4';
const CARD_TITLE = 'mb-2.5 flex items-center gap-2 text-body-sm font-semibold text-ink-900';
const NOTE = 'text-meta text-ink-500';

export interface ImportReviewProps {
  variant: 'engagement' | 'library';
  preview: ImportPreview;
  /** Refs of the likely duplicates the reviewer chose to import anyway. */
  keep: ReadonlySet<string>;
  acknowledgedRepeat: boolean;
  /** A changed mapping is being previewed again. */
  refreshing: boolean;
  /** The import is being confirmed: nothing can change. */
  disabled: boolean;
  /** Active library modules, for unknown module names (engagement only). */
  modules: ReadonlyArray<ModuleOption>;
  /** The last preview or confirm failure. */
  error: string | null;
  onOverrides: (change: (overrides: ImportOverrides) => ImportOverrides) => void;
  onKeep: (refs: ReadonlyArray<string>, keep: boolean) => void;
  onAcknowledgeRepeat: (acknowledged: boolean) => void;
  /** Drop this file and choose another. */
  onReplace: () => void;
}

export function ImportReview({
  variant,
  preview,
  keep,
  acknowledgedRepeat,
  refreshing,
  disabled,
  modules,
  error,
  onOverrides,
  onKeep,
  onAcknowledgeRepeat,
  onReplace,
}: ImportReviewProps) {
  const library = variant === 'library';
  const [tab, setTab] = React.useState<ImportTab>('all');
  const [limit, setLimit] = React.useState(PAGE_SIZE);
  const [headerRows, setHeaderRows] = React.useState(false);

  const totals = importTotals(preview, keep);
  const allRows = React.useMemo(() => displayRows(preview), [preview]);
  const tabRows = React.useMemo(() => rowsForTab(allRows, tab), [allRows, tab]);
  const flagged = React.useMemo(() => duplicateRefs(preview), [preview]);

  const skipped = preview.sheets.filter((s) => !s.used);
  const usedSheets = preview.sheets.filter((s) => s.used);
  // A guess worth checking. A column the library ignores anyway is not one.
  const isUnsure = (m: ImportPreview['mapping'][number]) =>
    m.confidence === 'low' &&
    m.source !== 'override' &&
    !(library && (m.field === 'criticality' || m.field === 'module'));
  const unsureColumns = preview.mapping.filter(isUnsure).length;
  const hasText = !preview.blockers.includes('no_text_column');
  const criticalityToCheck = preview.criticality_mapping.filter(
    (c) => c.value == null || (c.confidence === 'low' && c.source !== 'override'),
  ).length;
  const moduleChoices = preview.module_mapping.filter((m) => m.resolved_by !== 'exact');
  const modulesToChoose = moduleChoices.filter((m) => m.module_id == null && !m.skipped).length;
  const ignored = preview.ignored;
  const repeat = preview.repeat_of;

  const tabs: Array<{ id: ImportTab; label: string; count: number }> = [
    { id: 'all', label: 'All rows', count: allRows.length },
    { id: 'duplicates', label: 'Likely duplicates', count: totals.duplicates },
    ...(library
      ? [{ id: 'refused' as const, label: 'Already in this module', count: totals.refused }]
      : []),
    { id: 'excluded', label: 'Excluded', count: totals.excluded },
  ];

  const fieldOptions: ReadonlyArray<ImportField> = library
    ? IMPORT_FIELDS.filter((f) => f !== 'criticality' && f !== 'module')
    : IMPORT_FIELDS;

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            aria-hidden="true"
            className="flex h-11 w-9 flex-none items-end justify-center rounded border border-ink-200 bg-paper-100 pb-1 font-mono text-[9px] font-semibold text-ink-500"
          >
            {fileBadge(preview.file.file_name)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-h3 font-semibold text-ink-900">
              {preview.file.file_name}
            </div>
            <div className="text-meta font-medium text-ink-500">
              {plural(preview.counts.sheets_total, 'sheet')} ·{' '}
              {plural(preview.counts.rows_read, 'row')} read · {formatBytes(preview.file.byte_size)}
              {refreshing ? (
                <span className="ml-2 inline-flex items-center gap-1.5" role="status">
                  <Spinner /> Updating the preview…
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <WorkspaceButton variant="tertiary" disabled={disabled} onClick={onReplace}>
          Choose a different file
        </WorkspaceButton>
      </div>

      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}

      {repeat ? (
        <WorkspaceNotice tone="at-risk">
          <div className="font-semibold text-ink-900">This file was imported before</div>
          <p className="mt-0.5">
            {repeat.imported_by_name ?? 'Someone'} imported the same file
            {repeat.file_name && repeat.file_name !== preview.file.file_name
              ? ` (as ${repeat.file_name})`
              : ''}{' '}
            on {formatDate(repeat.imported_at)}
            {library && repeat.module_name ? ` into ${repeat.module_name}` : ''}:{' '}
            {plural(repeat.added, 'requirement')} added
            {repeat.first_number && repeat.last_number
              ? ` (${repeat.first_number} to ${repeat.last_number})`
              : ''}
            {repeat.still_active != null
              ? `, ${repeat.still_active.toLocaleString()} still active`
              : ''}
            .{' '}
            {library
              ? 'Rows it already created in this module are refused below.'
              : 'Rows that match what it created are flagged as likely duplicates below.'}
          </p>
          <label className="mt-2 flex w-fit cursor-pointer items-center gap-2 font-medium text-ink-900">
            <TriCheckbox
              state={acknowledgedRepeat ? 'on' : 'off'}
              aria-label="Import this file again"
              onChange={(next) => onAcknowledgeRepeat(next)}
            />
            Import this file again
          </label>
        </WorkspaceNotice>
      ) : null}

      <div className="grid grid-cols-2 gap-3 min-[900px]:grid-cols-4">
        <StatTile
          label="To import"
          value={totals.toImport}
          note={
            totals.duplicatesKept > 0
              ? `${preview.counts.to_import.toLocaleString()} new, ${totals.duplicatesKept.toLocaleString()} kept duplicates`
              : library
                ? 'new draft requirements'
                : 'new requirements'
          }
        />
        <StatTile
          label="Likely duplicates"
          value={totals.duplicates}
          note={
            totals.duplicates > 0
              ? `${totals.duplicatesSkipped.toLocaleString()} skipped, ${totals.duplicatesKept.toLocaleString()} kept`
              : 'none found'
          }
        />
        <StatTile
          label="Excluded"
          value={totals.excluded + totals.refused}
          note={
            totals.refused > 0
              ? `${totals.refused.toLocaleString()} already in this module`
              : totals.excluded > 0
                ? 'cannot be imported'
                : 'none'
          }
        />
        <StatTile
          label="Sheets used"
          value={`${preview.counts.sheets_used} of ${preview.counts.sheets_total}`}
          note="click a sheet to change"
        />
      </div>

      <section className={CARD} aria-label="Sheets">
        <h4 className={CARD_TITLE}>Sheets</h4>
        <div className="flex flex-wrap gap-x-1.5 gap-y-1.5">
          {preview.sheets.map((s) => (
            <button
              key={s.name}
              type="button"
              role="checkbox"
              aria-checked={s.used}
              aria-label={`Use sheet ${s.name}`}
              title={s.reason ?? undefined}
              disabled={disabled}
              onClick={() => onOverrides((o) => withSheetOverride(o, s.name, { use: !s.used }))}
              className={cn(
                'inline-flex h-7 items-center gap-2 rounded-full border border-ink-200 pl-2 pr-2.5 text-meta',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
                s.used ? 'bg-paper-0 text-ink-700' : 'bg-paper-50 text-ink-300',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-3.5 w-3.5 items-center justify-center rounded-sm border-[1.5px] text-paper-0',
                  s.used ? 'border-ink-900 bg-ink-900' : 'border-ink-300 bg-paper-0',
                )}
              >
                {s.used ? <Check className="h-2.5 w-2.5" strokeWidth={3.5} /> : null}
              </span>
              {s.name}
              <span className="font-mono text-[11px] text-ink-500">{s.data_row_count}</span>
            </button>
          ))}
        </div>
        <p className={cn(NOTE, 'mt-2.5')}>
          {skipped.length > 0 ? (
            <>
              Skipped:{' '}
              {skipped.map((s, i) => (
                <React.Fragment key={s.name}>
                  {i > 0 ? ' ' : ''}
                  <span className="font-medium text-ink-700">{s.name}</span> (
                  {s.reason ?? 'skipped'})
                </React.Fragment>
              ))}{' '}
            </>
          ) : (
            'Every sheet is used. '
          )}
          {library
            ? 'Every row goes into the module you are editing.'
            : preview.mapping.some((m) => m.field === 'module')
              ? 'The module is read from the module column.'
              : 'With no module column, the module is taken from the sheet name.'}{' '}
          {usedSheets.length > 0 ? (
            <button
              type="button"
              className="font-medium text-ink-700 underline underline-offset-[3px]"
              aria-expanded={headerRows}
              onClick={() => setHeaderRows((h) => !h)}
            >
              {headerRows ? 'Hide header rows' : 'Change header rows'}
            </button>
          ) : null}
        </p>
        {headerRows ? (
          <div className="mt-2.5 grid gap-1.5">
            {usedSheets.map((s) => (
              <HeaderRowField
                key={`${s.name}:${s.header_row ?? ''}`}
                name={s.name}
                headerRow={s.header_row}
                reason={s.reason ?? null}
                disabled={disabled}
                onCommit={(row) =>
                  onOverrides((o) => withSheetOverride(o, s.name, { header_row: row }))
                }
              />
            ))}
          </div>
        ) : null}
      </section>

      <div className="grid grid-cols-1 gap-4 min-[900px]:grid-cols-2">
        <section className={CARD} aria-label="Columns">
          <h4 className={CARD_TITLE}>
            Columns
            {!hasText ? (
              <span className={cn(CHIP, CHIP_CRITICAL)}>No requirement text</span>
            ) : unsureColumns > 0 ? (
              <span className={cn(CHIP, CHIP_AT_RISK)}>{unsureColumns} to check</span>
            ) : (
              <span className={cn(CHIP, CHIP_HEALTHY)}>Mapped</span>
            )}
          </h4>
          {preview.mapping.map((m) => {
            const unsure = isUnsure(m);
            const known = (IMPORT_FIELDS as readonly string[]).includes(m.field ?? '');
            const offered = known && !fieldOptions.includes(m.field as ImportField);
            return (
              <MapRow key={m.header} source={m.header || '(no header)'}>
                <select
                  aria-label={`Field for column ${m.header || '(no header)'}`}
                  data-unsure={unsure || undefined}
                  value={known ? (m.field ?? '') : ''}
                  disabled={disabled}
                  onChange={(e) =>
                    onOverrides((o) =>
                      withColumnMapping(
                        o,
                        m.header,
                        (e.target.value || null) as ImportField | null,
                      ),
                    )
                  }
                  className={cn(SELECT, unsure ? SELECT_UNSURE : SELECT_PLAIN)}
                >
                  <option value="">Not imported</option>
                  {offered ? (
                    <option value={m.field ?? ''}>
                      {FIELD_LABEL[m.field as ImportField]} (ignored)
                    </option>
                  ) : null}
                  {fieldOptions.map((f) => (
                    <option key={f} value={f}>
                      {FIELD_LABEL[f]}
                    </option>
                  ))}
                </select>
                {unsure ? (
                  <ConfirmGuess
                    label={`Confirm the mapping of ${m.header}`}
                    disabled={disabled}
                    onClick={() =>
                      onOverrides((o) =>
                        withColumnMapping(o, m.header, (m.field ?? null) as ImportField | null),
                      )
                    }
                  />
                ) : null}
              </MapRow>
            );
          })}
          {preview.mapping.length === 0 ? (
            <p className={NOTE}>No header row was found in the sheets in use.</p>
          ) : null}
          {unsureColumns > 0 ? (
            <p className={cn(NOTE, 'mt-2')}>
              Highlighted mappings were a best guess. Confirm or change them.
            </p>
          ) : null}
        </section>

        {library ? (
          <section className={CARD} aria-label="Not imported into the library">
            <h4 className={CARD_TITLE}>Not imported into the library</h4>
            {ignored?.criticality ? (
              <p className="text-body-sm text-ink-700">
                <span className={cn(CHIP, CHIP_MUTED, 'mr-1.5')}>Ignored</span>A criticality column
                was found ({ignored.criticality.labels.slice(0, 6).join(', ')}
                {ignored.criticality.labels.length > 6 ? ', …' : ''}) and is not imported. The
                library is neutral: criticality is set for each client, in the engagement.
              </p>
            ) : (
              <p className="text-body-sm text-ink-700">
                The library is neutral: it holds no criticality. A criticality column, if the file
                had one, would be ignored.
              </p>
            )}
            {ignored?.module ? (
              <p className="mt-2 text-body-sm text-ink-700">
                <span className={cn(CHIP, CHIP_AT_RISK, 'mr-1.5')}>Check</span>
                The file names other modules ({ignored.module.names.slice(0, 6).join(', ')}
                {ignored.module.names.length > 6 ? ', …' : ''}). Every row still goes into the
                module you are editing; skip the sheets that belong elsewhere.
              </p>
            ) : null}
            {preview.counts.with_metadata ? (
              <p className="mt-2 text-body-sm text-ink-700">
                <span className={cn(CHIP, CHIP_MUTED, 'mr-1.5')}>Moved</span>
                {preview.counts.with_metadata.toLocaleString()} rows end in labelled metadata (Tag,
                Live, Basis, Proof, Pending). It is kept as each requirement&rsquo;s metadata, not
                in its wording, so it never reaches a client&rsquo;s RTM.
              </p>
            ) : null}
            <p className={cn(NOTE, 'mt-2.5')}>
              The original file is not kept. Each requirement records the file name, sheet and row
              it came from.
            </p>
          </section>
        ) : (
          <section className={CARD} aria-label="Criticality values">
            <h4 className={CARD_TITLE}>
              Criticality values
              {preview.criticality_mapping.length === 0 ? null : criticalityToCheck > 0 ? (
                <span className={cn(CHIP, CHIP_AT_RISK)}>{criticalityToCheck} to check</span>
              ) : (
                <span className={cn(CHIP, CHIP_HEALTHY)}>Mapped</span>
              )}
            </h4>
            {preview.criticality_mapping.map((c) => {
              const unsure = c.value != null && c.confidence === 'low' && c.source !== 'override';
              return (
                <MapRow key={c.label} source={c.label} count={c.count}>
                  <select
                    aria-label={`Criticality for ${c.label}`}
                    data-unsure={unsure || undefined}
                    value={c.value ?? ''}
                    disabled={disabled}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) onOverrides((o) => withCriticality(o, c.label, value));
                    }}
                    className={cn(
                      SELECT,
                      c.value == null ? SELECT_REQUIRED : unsure ? SELECT_UNSURE : SELECT_PLAIN,
                    )}
                  >
                    {c.value == null ? <option value="">Choose…</option> : null}
                    {IMPORT_CRITICALITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {unsure ? (
                    <ConfirmGuess
                      label={`Confirm the criticality of ${c.label}`}
                      disabled={disabled}
                      onClick={() => onOverrides((o) => withCriticality(o, c.label, c.value!))}
                    />
                  ) : null}
                </MapRow>
              );
            })}
            {preview.criticality_mapping.length === 0 ? (
              <p className={NOTE}>
                No criticality column in this file. Every requirement starts as Not set; set it in
                the table afterwards.
              </p>
            ) : (
              <p className={cn(NOTE, 'mt-2')}>
                Asked once for each label in the file. Blank cells become Not set.
              </p>
            )}
          </section>
        )}
      </div>

      {!library && moduleChoices.length > 0 ? (
        <section className={CARD} aria-label="Modules">
          <h4 className={CARD_TITLE}>
            Modules
            {modulesToChoose > 0 ? (
              <span className={cn(CHIP, CHIP_CRITICAL)}>{modulesToChoose} to choose</span>
            ) : (
              <span className={cn(CHIP, CHIP_HEALTHY)}>Mapped</span>
            )}
          </h4>
          {moduleChoices.map((m) => {
            const value = m.skipped ? SKIP_MODULE : m.module_id;
            return (
              <MapRow key={m.name} source={m.name} count={m.count}>
                <ModuleSelect
                  aria-label={`Library module for ${m.name}`}
                  value={value}
                  modules={modules}
                  required
                  disabled={disabled}
                  onChange={(next) =>
                    onOverrides((o) => withModule(o, m.name, next === SKIP_MODULE ? null : next))
                  }
                />
                {value == null && m.suggestion ? (
                  <WorkspaceButton
                    disabled={disabled}
                    onClick={() =>
                      onOverrides((o) => withModule(o, m.name, m.suggestion!.module_id))
                    }
                  >
                    Use {m.suggestion.module_name}
                  </WorkspaceButton>
                ) : null}
              </MapRow>
            );
          })}
          <p className={cn(NOTE, 'mt-2')}>
            These names are not library modules. Choose where their rows go, once per name, or leave
            them out.
          </p>
        </section>
      ) : null}

      <section
        className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
        aria-label="Rows"
      >
        <div className="flex items-center gap-5 border-b border-ink-100 px-4">
          <div role="tablist" aria-label="Rows to show" className="flex min-w-0 flex-1 gap-5">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => {
                  setTab(t.id);
                  setLimit(PAGE_SIZE);
                }}
                className={cn(
                  '-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 py-2.5 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  tab === t.id
                    ? 'border-accent-amber-600 font-semibold text-ink-900'
                    : 'border-transparent font-medium text-ink-500 hover:text-ink-700',
                )}
              >
                {t.label}
                <span className="rounded-full bg-ink-100 px-1.5 py-px text-[11px] font-medium text-ink-500">
                  {t.count.toLocaleString()}
                </span>
              </button>
            ))}
          </div>
          {flagged.length > 0 ? (
            <div className="flex flex-none items-center gap-1">
              <WorkspaceButton
                variant="tertiary"
                disabled={disabled || totals.duplicatesSkipped === 0}
                onClick={() => onKeep(flagged, true)}
              >
                Import all duplicates
              </WorkspaceButton>
              <WorkspaceButton
                variant="tertiary"
                disabled={disabled || totals.duplicatesKept === 0}
                onClick={() => onKeep(flagged, false)}
              >
                Skip all duplicates
              </WorkspaceButton>
            </div>
          ) : null}
        </div>
        <table className="w-full table-fixed border-collapse text-left text-body-sm">
          <thead>
            <tr className="border-b border-ink-100 text-meta font-medium text-ink-500">
              <th className="w-[92px] px-4 py-2 font-medium">Client ref</th>
              <th className="w-[170px] px-2 py-2 font-medium">
                {library ? 'Sub-category' : 'Module › sub-category'}
              </th>
              <th className="px-2 py-2 font-medium">Requirement</th>
              {library ? null : <th className="w-[124px] px-2 py-2 font-medium">Criticality</th>}
              <th className="w-[250px] px-2 py-2 pr-4 font-medium">Result</th>
            </tr>
          </thead>
          <tbody>
            {tabRows.slice(0, limit).map((r) => (
              <PreviewRow
                key={r.ref}
                row={r}
                library={library}
                kept={keep.has(r.ref)}
                disabled={disabled}
                onKeep={onKeep}
              />
            ))}
            {tabRows.length === 0 ? (
              <tr>
                <td
                  colSpan={library ? 4 : 5}
                  className="px-4 py-6 text-center text-meta text-ink-500"
                >
                  Nothing in this view.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {tabRows.length > limit ? (
          <div className="flex items-center justify-center gap-3 border-t border-ink-100 px-4 py-2.5 text-meta text-ink-500">
            Showing the first {limit.toLocaleString()} of {tabRows.length.toLocaleString()} rows.
            <WorkspaceButton onClick={() => setLimit((n) => n + PAGE_SIZE)}>
              Show more
            </WorkspaceButton>
          </div>
        ) : null}
      </section>
    </>
  );
}

function StatTile({ label, value, note }: { label: string; value: number | string; note: string }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-card border border-ink-100 bg-paper-0 px-5 py-4">
      <div className="text-meta font-medium text-ink-500">{label}</div>
      <div className="text-[24px] font-semibold leading-[30px] tracking-[-0.01em] tabular-nums text-ink-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-meta text-ink-500">{note}</div>
    </div>
  );
}

/** One "value in the file -> what it becomes" row of a mapping card. */
function MapRow({
  source,
  count,
  children,
}: {
  source: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_16px_minmax(0,1.3fr)] items-center gap-2.5 py-[5px] text-body-sm">
      <span className="truncate font-mono text-meta text-ink-700" title={source}>
        {source}
        {count != null ? (
          <span className="ml-1.5 text-[11px] text-ink-500">{count.toLocaleString()}</span>
        ) : null}
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-ink-300" aria-hidden="true" />
      <span className="flex min-w-0 items-center gap-1.5 [&>*:first-child]:flex-1">{children}</span>
    </div>
  );
}

function ConfirmGuess({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <WorkspaceButton
      aria-label={label}
      title="This guess is right"
      disabled={disabled}
      onClick={onClick}
    >
      Looks right
    </WorkspaceButton>
  );
}

function HeaderRowField({
  name,
  headerRow,
  reason,
  disabled,
  onCommit,
}: {
  name: string;
  headerRow: number | null;
  reason: string | null;
  disabled: boolean;
  onCommit: (row: number) => void;
}) {
  const [value, setValue] = React.useState(headerRow != null ? String(headerRow) : '');
  const commit = () => {
    const row = Number(value);
    if (Number.isInteger(row) && row >= 1 && row !== headerRow) onCommit(row);
    else setValue(headerRow != null ? String(headerRow) : '');
  };
  return (
    <label className="flex items-center gap-2.5 text-meta text-ink-700">
      <span className="w-[200px] flex-none truncate font-medium" title={name}>
        {name}
      </span>
      <span className="flex-none text-ink-500">header on row</span>
      <input
        type="number"
        min={1}
        value={value}
        disabled={disabled}
        aria-label={`Header row of ${name}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        className={cn(SELECT, SELECT_PLAIN, 'w-[72px] flex-none font-mono')}
      />
      {reason ? <span className="min-w-0 truncate text-ink-500">{reason}</span> : null}
    </label>
  );
}

const PreviewRow = React.memo(function PreviewRow({
  row,
  library,
  kept,
  disabled,
  onKeep,
}: {
  row: DisplayRow;
  library: boolean;
  kept: boolean;
  disabled: boolean;
  onKeep: (refs: ReadonlyArray<string>, keep: boolean) => void;
}) {
  const leftOut =
    row.kind === 'excluded' || row.kind === 'refused' || (row.kind === 'duplicate' && !kept);
  const best = row.duplicates[0];
  const name = row.clientRef ?? `row ${row.row}`;
  return (
    <tr
      data-ref={row.ref}
      data-kind={row.kind}
      className={cn('border-b border-ink-100 align-top last:border-b-0', leftOut && 'text-ink-300')}
    >
      <td className="px-4 py-2 font-mono text-meta" title={`${row.sheet}, row ${row.row}`}>
        {row.clientRef ?? <span className="text-ink-300">row {row.row}</span>}
      </td>
      <td className="px-2 py-2 leading-5">
        {library ? (
          (row.subcategory ?? '')
        ) : (
          <>
            <span className={leftOut ? undefined : 'text-ink-900'}>{row.module ?? ''}</span>
            {row.subcategory ? (
              <span className={cn('block text-meta', !leftOut && 'text-ink-500')}>
                {row.subcategory}
              </span>
            ) : null}
          </>
        )}
      </td>
      <td className="px-2 py-2 leading-5">
        <span className={cn(!leftOut ? 'text-ink-900' : row.kind !== 'excluded' && 'line-through')}>
          {row.text || <em className="not-italic">No requirement text</em>}
        </span>
        {row.metadata ? (
          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-meta text-ink-500">
            {metadataEntries(row.metadata).map((m) => (
              <span key={m.field}>
                <span className={cn('font-medium', !leftOut && 'text-ink-700')}>{m.label}</span>{' '}
                {m.value}
              </span>
            ))}
          </span>
        ) : null}
        {row.kind === 'duplicate' && best ? (
          <div className="mt-1.5 rounded-card border border-l-4 border-ink-100 border-l-posture-at-risk bg-paper-50 px-2.5 py-1.5 text-meta text-ink-700 no-underline">
            <span className="font-medium text-ink-900">
              {best.kind === 'in_file' ? 'Resembles ' : ''}
              <span className={best.kind === 'in_file' ? undefined : 'font-mono'}>
                {duplicateTarget(best)}
              </span>
            </span>
            {best.module_name ? ` · ${best.module_name}` : ''} · {duplicateScore(best)}
            <span className="mt-0.5 block text-ink-700">{best.text}</span>
            {row.duplicates.length > 1 ? (
              <span className="mt-0.5 block text-ink-500">
                Also resembles{' '}
                {row.duplicates
                  .slice(1)
                  .map((d) => duplicateTarget(d))
                  .join(', ')}
                .
              </span>
            ) : null}
          </div>
        ) : null}
      </td>
      {library ? null : (
        <td className="px-2 py-2">
          {row.kind === 'excluded' ? null : row.criticality ? (
            <CriticalityChip value={asCriticality(row.criticality)} />
          ) : row.criticalityLabel ? (
            <span className={cn(CHIP, CHIP_CRITICAL)} title="This label is not mapped yet">
              {row.criticalityLabel}?
            </span>
          ) : null}
        </td>
      )}
      <td className="px-2 py-2 pr-4">
        {row.kind === 'new' ? (
          <span className={cn(CHIP, CHIP_HEALTHY)}>Will import</span>
        ) : row.kind === 'needs_mapping' ? (
          <span className={cn(CHIP, CHIP_CRITICAL)}>Needs a mapping</span>
        ) : row.kind === 'duplicate' && best ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className={cn(CHIP, kept ? CHIP_HEALTHY : CHIP_AT_RISK, 'max-w-full')}>
              <span className="truncate">{kept ? 'Will import' : duplicateLabel(best)}</span>
            </span>
            <WorkspaceButton
              variant="tertiary"
              disabled={disabled}
              aria-label={kept ? `Skip ${name}` : `Import ${name} anyway`}
              onClick={() => onKeep([row.ref], !kept)}
            >
              {kept ? 'Skip it' : 'Import anyway'}
            </WorkspaceButton>
          </span>
        ) : (
          <span className="flex flex-col items-start gap-1">
            <span className={cn(CHIP, CHIP_MUTED)}>
              {row.kind === 'refused' ? 'Refused' : 'Excluded'}
            </span>
            {row.reason ? <span className="text-meta text-ink-500">{row.reason}</span> : null}
          </span>
        )}
      </td>
    </tr>
  );
});
